import React, { useEffect, useMemo, useState } from "react";
import { Card, Button, Form, InputGroup, Alert, Row, Col, Table, Badge } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { IconKey, IconRefresh, IconPlugConnected, IconPlugConnectedX, IconClipboard } from "@tabler/icons-react";
import { fetchWithAuth } from "../utils/fetchWithAuth";

// Fixed base URL
const API_BASE = "https://app.indexbooster.net/api";

const CodeBlock = ({ title, code }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* ignore */ }
  };
  return (
    <Card className="mb-3 shadow-sm">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <strong>{title}</strong>
        <Button size="sm" variant={copied ? "success" : "outline-secondary"} onClick={onCopy}>
          <IconClipboard size={16} className="me-1" />
          {copied ? "Copied" : "Copy"}
        </Button>
      </Card.Header>
      <Card.Body style={{ padding: "0.75rem" }}>
        <pre className="mb-0" style={{ whiteSpace: "pre-wrap" }}><code>{code}</code></pre>
      </Card.Body>
    </Card>
  );
};

const APIIndexBooster = () => {
  const [apiKey, setApiKey] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const [rateLimit, setRateLimit] = useState(null);

  // ---------- Helpers for nicely wrapped cURL -----------
  const curlHeaders = useMemo(() => {
    const key = apiKey ? apiKey : "YOUR_API_KEY";
    return `-H "X-API-Key: ${key}" -H "Content-Type: application/json"`;
  }, [apiKey]);

  const curlJoin = (...lines) => lines.join(" \
");

  const curlSubmit = useMemo(() => curlJoin(
    `curl -X POST "${API_BASE}/v1/submit-url"`,
    curlHeaders,
    `-d '{`,
    `  "urls": ["https://example.com/", "https://example.com/page-1"]`,
    `}'`
  ), [curlHeaders]);

  const curlCreateProject = useMemo(() => curlJoin(
    `curl -X POST "${API_BASE}/v1/projects/create"`,
    curlHeaders,
    `-d '{`,
    `  "name": "My API Project",`,
    `  "description": "Created via API",`,
    `  "urls": ["https://example.com/","https://example.com/page-1"]`,
    `}'`
  ), [curlHeaders]);

  const curlSubmitToProject = useMemo(() => curlJoin(
    `curl -X POST "${API_BASE}/v1/projects/123/submit-url"`,
    curlHeaders,
    `-d '{`,
    `  "urls": ["https://example.com/new-page"]`,
    `}'`
  ), [curlHeaders]);

  const curlListProjects = useMemo(() => curlJoin(
    `curl -X GET "${API_BASE}/v1/projects"`,
    curlHeaders
  ), [curlHeaders]);

  const curlGetProjectUrls = useMemo(() => curlJoin(
    `curl -X GET "${API_BASE}/v1/projects/123/urls"`,
    curlHeaders
  ), [curlHeaders]);

  const curlCredits = useMemo(() => curlJoin(
    `curl -X GET "${API_BASE}/v1/credits"`,
    curlHeaders
  ), [curlHeaders]);

  const pythonExample = useMemo(() => {
    const key = apiKey ? apiKey : "YOUR_API_KEY";
    return `import requests

API_BASE = "${API_BASE}"
API_KEY = "${key}"

headers = {"X-API-Key": API_KEY, "Content-Type": "application/json"}

# 1) Submit to default API project (project "API" will be created if missing)
r = requests.post(f"{API_BASE}/v1/submit-url", json={
  "urls": ["https://example.com/", "https://example.com/page-1"]
}, headers=headers)
print("submit default:", r.status_code, r.json())

# 2) Create a new named project and submit URLs
r = requests.post(f"{API_BASE}/v1/projects/create", json={
  "name": "My API Project",
  "description": "Created from script",
  "urls": ["https://example.com/a","https://example.com/b"]
}, headers=headers)
print("create project:", r.status_code, r.json())

# 3) Add more URLs to existing project (id=123)
r = requests.post(f"{API_BASE}/v1/projects/123/submit-url", json={"urls": ["https://example.com/new"]}, headers=headers)
print("add to project:", r.status_code, r.json())

# 4) Get remaining credits
r = requests.get(f"{API_BASE}/v1/credits", headers=headers)
print("credits:", r.status_code, r.json())`;
  }, [apiKey]);

  const nodeExample = useMemo(() => {
    const key = apiKey ? apiKey : "YOUR_API_KEY";
    return `const fetch = (...args) => import('node-fetch').then(({default: fetch}) => fetch(...args));

const API_BASE = "${API_BASE}";
const API_KEY = "${key}";

const headers = { 'X-API-Key': API_KEY, 'Content-Type': 'application/json' };

(async () => {
  // Default API project
  let res = await fetch(API_BASE + '/v1/submit-url', {
    method: 'POST', headers, body: JSON.stringify({ urls: ['https://example.com/'] })
  });
  console.log('submit default:', await res.json());

  // Named project
  res = await fetch(API_BASE + '/v1/projects/create', {
    method: 'POST', headers, body: JSON.stringify({
      name: 'My API Project',
      description: 'Created via Node',
      urls: ['https://example.com/x','https://example.com/y']
    })
  });
  console.log('create project:', await res.json());
})();`;
  }, [apiKey]);

  // --------- API key controls ---------
  const toggleApi = async () => {
    try {
      setLoading(true);
      const res = await fetchWithAuth("/api/toggle-api-access", { method: "POST" });
      const data = await res.json();
      setApiKey(data.api_key || "");
      setEnabled(!!data.enabled);
    } catch (_) { /* ignore */ }
    finally { setLoading(false); }
  };

  const regenerateApiKey = async () => {
    try {
      setLoading(true);
      const res = await fetchWithAuth("/api/regenerate-api-key", { method: "POST" });
      const data = await res.json();
      setApiKey(data.api_key || "");
    } catch (_) { /* ignore */ }
    finally { setLoading(false); }
  };

  const copyKey = async () => {
    setCopiedKey(false);
    setCopyError(false);
    try {
      await navigator.clipboard.writeText(apiKey || "");
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 1500);
    } catch {
      setCopyError(true);
      setTimeout(() => setCopyError(false), 2500);
    }
  };

  const getApiStatus = async () => {
    try {
      const res = await fetchWithAuth("/api/api-key-status");
      const data = await res.json();
      setApiKey(data.api_key || "");
      setEnabled(!!data.enabled);
      setRateLimit(data.rate_limit || null);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => { getApiStatus(); }, []);

  return (
    <>
      <Helmet><title>API – IndexBooster</title></Helmet>

      <Card className="mt-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h3 className="m-0"><IconKey size={24} className="me-2" />API Access</h3>
          <Button variant={enabled ? "danger" : "success"} onClick={toggleApi} disabled={loading}>
            {enabled ? (<><IconPlugConnectedX className="me-1" size={18} /> Disable</>) : (<><IconPlugConnected className="me-1" size={18} /> Enable</>)}
          </Button>
        </Card.Header>
        <Card.Body>
          {enabled ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Your API Key</Form.Label>
                <InputGroup>
                  <Form.Control value={apiKey} readOnly />
                  <Button onClick={copyKey} disabled={!apiKey}>Copy</Button>
                </InputGroup>
                {copiedKey && <Alert variant="success" className="mt-2 py-1 px-2">Copied!</Alert>}
                {copyError && <Alert variant="warning" className="mt-2 py-1 px-2">Clipboard is unavailable. Copy manually.</Alert>}
              </Form.Group>

              <Button variant="outline-primary" onClick={regenerateApiKey} disabled={loading}>
                <IconRefresh className="me-1" size={18} /> Regenerate Key
              </Button>

              <hr />
              <p className="mb-1"><strong>Base URL:</strong> <code>{API_BASE}</code></p>
              <p className="mb-3"><strong>Auth header:</strong> <code>X-API-Key: {apiKey || "YOUR_API_KEY"}</code></p>

              <Alert variant="info">
                <div className="fw-bold mb-1">Default API project</div>
                Using <code>POST /v1/submit-url</code> will create (or reuse) your personal project named <code>API</code>.
                All URLs sent to this endpoint are added into this <em>default API project</em> automatically.
              </Alert>

              <Row>
                <Col md={6}>
                  <h5>Quick Start (cURL)</h5>
                  <CodeBlock title="Submit to default API project" code={curlSubmit} />
                  <CodeBlock title="Create a project with URLs" code={curlCreateProject} />
                  <CodeBlock title="Submit URLs to existing project (id=123)" code={curlSubmitToProject} />
                  <CodeBlock title="List projects" code={curlListProjects} />
                  <CodeBlock title="Get project URLs (id=123)" code={curlGetProjectUrls} />
                  <CodeBlock title="Check remaining credits" code={curlCredits} />
                </Col>
                <Col md={6}>
                  <h5>Quick Start (Python)</h5>
                  <CodeBlock title="Python requests example" code={pythonExample} />

                  <h5 className="mt-3">Quick Start (Node.js)</h5>
                  <CodeBlock title="node-fetch example" code={nodeExample} />
                </Col>
              </Row>

              <hr />
              <h4 className="mb-2">Endpoints (detailed)</h4>

              <Table bordered responsive className="mb-3">
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Path</th>
                    <th>What it does</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><Badge bg="dark text-white">POST</Badge></td>
                    <td><code>/v1/submit-url</code></td>
                    <td>Submit URLs to your default <code>API</code> project. Creates it if missing.</td>
                    <td>Max 2000 URLs; 1 credit per new URL; duplicates in “Request in progress” are skipped.</td>
                  </tr>
                  <tr>
                    <td><Badge bg="dark text-white">POST</Badge></td>
                    <td><code>/v1/projects/create</code></td>
                    <td>Create a named project and submit URLs in one request.</td>
                    <td>Project name must be unique per user.</td>
                  </tr>
                  <tr>
                    <td><Badge bg="dark text-white">POST</Badge></td>
                    <td><code>/v1/projects/&lt;project_id&gt;/submit-url</code></td>
                    <td>Add URLs to an existing project (must belong to you).</td>
                    <td>Same rules as default submit.</td>
                  </tr>
                  <tr>
                    <td><Badge bg="primary text-white">GET</Badge></td>
                    <td><code>/v1/projects</code></td>
                    <td>List your projects.</td>
                    <td>Returns id, name, description, created/updated ISO timestamps.</td>
                  </tr>
                  <tr>
                    <td><Badge bg="primary text-white">GET</Badge></td>
                    <td><code>/v1/projects/&lt;project_id&gt;/urls</code></td>
                    <td>List URLs of a given project with indexing info.</td>
                    <td>See fields: indexable, indexing_status, snippet_info.</td>
                  </tr>
                  <tr>
                    <td><Badge bg="primary text-white">GET</Badge></td>
                    <td><code>/v1/credits</code></td>
                    <td>Get your remaining credit balance.</td>
                    <td>Simple JSON: {"{ credits: number }"}.</td>
                  </tr>
                </tbody>
              </Table>

              <h5>Request & Response details</h5>
              <ul>
                <li><strong>URL list rules:</strong> up to 2000 URLs per request; must start with <code>http://</code> or <code>https://</code>; we normalize the root to a trailing slash (<code>https://site.com/</code>).</li>
                <li><strong>Credits:</strong> 1 credit per submitted URL (only for truly new items). URLs already in “Request in progress” are skipped without extra charges.</li>
                <li><strong>On success</strong> (<code>200</code>): you get counts (<code>submitted</code>, <code>deducted_credits</code>) and list of created records with their <code>id</code> and <code>url</code>. Default endpoint also returns <code>project_id</code>.</li>
                <li><strong>Errors:</strong> <code>400</code> invalid input; <code>403</code> invalid/disabled key or insufficient credits; <code>404</code> project not found; <code>429</code> rate limit exceeded (100 req/min).</li>
              </ul>
            </>
          ) : (
            <p className="text-muted mb-0">API access is disabled. Click <strong>Enable</strong> to generate your API key.</p>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default APIIndexBooster;
